<template>
  <div>
    <Nav></Nav>
    <b-container fluid class="mt-3">
      <b-row align-v="center" class="mb-2">
        <b-col>
          <h3 class="mb-0">
            <b-icon icon="table" class="mr-1"></b-icon>
            Menú · {{ sucursal.nombre || ("Sucursal " + sucursalId) }}
          </h3>
          <small class="text-muted">
            Precio de cada producto por área en esta sucursal. Haz clic en un precio para editarlo;
            los cambios se guardan de inmediato y el POS los recibe en su siguiente actualización.
          </small>
        </b-col>
        <b-col cols="auto">
          <b-button size="sm" variant="outline-secondary" class="mr-1" @click="$router.push('/pos')">
            <b-icon icon="arrow-left"></b-icon> Cambiar sucursal
          </b-button>
          <b-button size="sm" variant="outline-primary" class="mr-1" @click="$router.push('/pos/captura/' + sucursalId)">
            <b-icon icon="hand-index-thumb"></b-icon> Simular captura
          </b-button>
          <b-button size="sm" variant="outline-primary" :disabled="loading" @click="loadAll">
            <b-icon icon="arrow-clockwise"></b-icon> Recargar
          </b-button>
        </b-col>
      </b-row>

      <b-alert :show="loadError !== ''" variant="danger" dismissible @dismissed="loadError = ''">{{ loadError }}</b-alert>

      <b-overlay :show="loading" rounded="sm">
        <b-card no-body class="mb-2 p-2">
          <b-row align-v="end">
            <b-col md="4" sm="6">
              <b-form-group label="Buscar" class="mb-1" label-size="sm">
                <b-form-input v-model="search" debounce="300" size="sm" type="search"
                  placeholder="Nombre, clave o código"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="3" sm="6">
              <b-form-group label="Grupo" class="mb-1" label-size="sm">
                <b-form-select v-model="groupFilter" :options="groupOptions" size="sm"></b-form-select>
              </b-form-group>
            </b-col>
            <b-col md="auto" class="mb-1">
              <b-badge pill variant="success">Sigue master</b-badge>
              <b-badge pill variant="danger">Único</b-badge>
              <b-badge pill variant="secondary">Deshabilitado</b-badge>
              <b-badge pill variant="light" class="border">Sin precio</b-badge>
            </b-col>
          </b-row>
        </b-card>

        <!-- Acciones por área sobre los productos filtrados -->
        <b-card no-body bg-variant="light" class="mb-2 py-2 px-3">
          <b-row align-v="end">
            <b-col md="3" sm="12">
              <b-form-group label="Acción masiva en área:" class="mb-1" label-size="sm">
                <b-form-select size="sm" v-model="batchArea" :options="areaOptions"></b-form-select>
              </b-form-group>
            </b-col>
            <b-col md="auto" sm="12" class="mb-1">
              <b-button size="sm" variant="success" @click="batchFollowMaster" :disabled="batchProcessing || !batchArea"
                v-b-tooltip.hover title="Los productos filtrados seguirán el precio master de esta área en esta sucursal">
                <b-icon icon="diagram3"></b-icon> Seguir master
              </b-button>
              <b-button size="sm" variant="outline-success" @click="batchToggleStatus(true)" :disabled="batchProcessing || !batchArea"
                v-b-tooltip.hover title="Habilitar el precio de esta área en los productos filtrados">
                <b-icon icon="check-circle"></b-icon> Habilitar
              </b-button>
              <b-button size="sm" variant="outline-secondary" @click="batchToggleStatus(false)" :disabled="batchProcessing || !batchArea"
                v-b-tooltip.hover title="Deshabilitar el precio de esta área en los productos filtrados">
                <b-icon icon="x-circle"></b-icon> Deshabilitar
              </b-button>
            </b-col>
            <b-col md="auto" class="mb-1 small text-muted">
              Aplica a los {{ filteredRows.length }} productos filtrados.
              <b-spinner v-if="batchProcessing" small variant="primary" class="ml-1"></b-spinner>
            </b-col>
          </b-row>
        </b-card>

        <b-table sticky-header="70vh" small hover responsive :items="pagedRows" :fields="tableFields"
          class="menu-table bg-white" show-empty empty-text="Sin productos que coincidan">
          <template #cell(product)="{ item }">
            <div class="text-nowrap">
              <strong>{{ item.product.ndescription }}</strong>
            </div>
            <small class="text-muted">{{ item.product.clave }}<span v-if="item.product.barcode"> · {{ item.product.barcode }}</span></small>
          </template>

          <template v-for="area in areas" v-slot:[`cell(a${area.id})`]="{ item }">
            <div v-if="editingKey === cellKey(item, area)" :key="'edit-' + area.id" class="d-flex align-items-center">
              <b-form-input size="sm" type="number" step="0.01" v-model.number="editingValue" autofocus style="max-width: 100px"
                @keyup.enter="saveInline(item, area)" @blur="saveInline(item, area)" @keyup.escape="cancelInline"></b-form-input>
            </div>
            <div v-else :key="'disp-' + area.id" class="d-flex align-items-center">
              <b-badge :variant="cellVariant(item, area)" class="price-badge" :class="{ 'border text-dark': !item.cells[area.id] }"
                v-b-tooltip.hover :title="cellTitle(item, area)" @click="startEdit(item, area)">
                {{ cellText(item, area) }}
              </b-badge>
              <b-button size="sm" variant="link" class="p-0 ml-1" @click="openOptions(item, area)" v-b-tooltip.hover title="Opciones">
                <b-icon icon="three-dots-vertical" font-scale="0.8"></b-icon>
              </b-button>
            </div>
          </template>
        </b-table>
        <b-row align-v="center">
          <b-col>
            <b-pagination v-model="page" :total-rows="filteredRows.length" :per-page="perPage" size="sm" class="mb-0"></b-pagination>
          </b-col>
          <b-col cols="auto" class="small text-muted">{{ filteredRows.length }} productos</b-col>
        </b-row>
      </b-overlay>
    </b-container>

    <!-- Opciones de un precio (misma forma que el modal de la ficha de producto) -->
    <b-modal id="modal-menu-price" :title="optionsTitle" @ok.prevent="saveOptions" ok-title="Guardar" cancel-title="Cancelar"
      :ok-disabled="saving">
      <b-row>
        <b-col>
          <b-form-group label="Master:">
            <b-form-checkbox v-model="actualPrice.follow_price">Sigue el precio master</b-form-checkbox>
            <small v-if="optionsMaster" class="text-muted">Precio master del área: ${{ Number(optionsMaster.price).toFixed(2) }}</small>
            <small v-else class="text-danger">Este producto no tiene precio master en el área.</small>
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group label="Status:">
            <b-form-checkbox v-model="actualPrice.status">Habilitado</b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row v-if="!actualPrice.follow_price">
        <b-col>
          <b-form-group label="Precio:">
            <b-form-input v-model.number="actualPrice.price" type="number" step="0.01" placeholder="Precio" required></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>
      <b-button v-if="actualPrice.id > 0" size="sm" variant="outline-danger" @click="deletePrice">
        <b-icon icon="trash"></b-icon> Eliminar precio de esta sucursal
      </b-button>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import Nav from "@/components/Nav.vue";
import { LAST_SUCURSAL_KEY } from "@/utils/posPreview";

export default {
  name: "MenuSucursal",
  components: { Nav },
  data() {
    return {
      sucursalId: parseInt(this.$route.params.sucursal_id, 10) || 0,
      sucursal: {},
      loading: false,
      saving: false,
      loadError: "",
      products: [],
      groups: [],
      areas: [],
      masterPrices: [],
      branchPrices: [],
      search: (this.$route.query.q || "").toString(),
      groupFilter: null,
      page: 1,
      perPage: 50,
      editingKey: null,
      editingValue: 0,
      actualPrice: this.emptyPrice(),
      optionsProduct: null,
      optionsArea: null,
      batchArea: null,
      batchProcessing: false
    };
  },
  computed: {
    tableFields() {
      return [{ key: "product", label: "Producto", stickyColumn: true }].concat(
        this.areas.map(a => ({ key: "a" + a.id, label: a.name }))
      );
    },
    areaOptions() {
      return [{ value: null, text: "Selecciona un área" }].concat(this.areas.map(a => ({ value: a.id, text: a.name })));
    },
    groupOptions() {
      return [{ value: null, text: "Todos los grupos" }].concat(
        this.groups.slice().sort((a, b) => a.ordenation - b.ordenation).map(g => ({ value: g.id, text: g.button_text || g.name }))
      );
    },
    // Una fila por producto con un mapa area_id -> registro de precio de la sucursal.
    rows() {
      const byProduct = {};
      this.branchPrices.forEach(p => {
        if (!byProduct[p.product_id]) byProduct[p.product_id] = {};
        byProduct[p.product_id][p.area_restaurant_id] = p;
      });
      return this.products
        .slice()
        .sort((a, b) => (a.ndescription || "").localeCompare(b.ndescription || ""))
        .map(p => ({ product: p, cells: byProduct[p.id] || {} }));
    },
    filteredRows() {
      const q = (this.search || "").toString().trim().toLowerCase();
      return this.rows.filter(r => {
        const p = r.product;
        if (this.groupFilter && p.group !== this.groupFilter) return false;
        if (!q) return true;
        return [p.ndescription, p.clave, p.barcode, p.short_name].some(v => (v || "").toString().toLowerCase().includes(q));
      });
    },
    pagedRows() {
      const start = (this.page - 1) * this.perPage;
      return this.filteredRows.slice(start, start + this.perPage);
    },
    optionsTitle() {
      if (!this.optionsProduct || !this.optionsArea) return "Precio";
      return this.optionsProduct.ndescription + " · " + this.optionsArea.name;
    },
    optionsMaster() {
      if (!this.optionsProduct || !this.optionsArea) return null;
      return this.masterPrice(this.optionsProduct.id, this.optionsArea.id);
    }
  },
  watch: {
    search() {
      this.page = 1;
    },
    groupFilter() {
      this.page = 1;
    }
  },
  mounted() {
    if (!this.sucursalId) {
      this.$router.replace("/pos").catch(() => {});
      return;
    }
    try {
      localStorage.setItem(LAST_SUCURSAL_KEY, String(this.sucursalId));
    } catch (e) {
      // sin localStorage no pasa nada
    }
    this.loadAll();
  },
  methods: {
    emptyPrice() {
      return {
        id: 0,
        product_id: 0,
        area_restaurant_id: 0,
        sucursal_id: this.sucursalId,
        price: 0,
        price_wo_tax: 0,
        tax: 0,
        tax_excempt: false,
        billable: true,
        follow_price: true,
        status: true
      };
    },
    async loadAll() {
      this.loading = true;
      this.loadError = "";
      try {
        const [sucursal, products, branchPrices, masterPrices, areas, groups] = await Promise.all([
          axios.get("/sucursal/" + this.sucursalId).then(r => r.data),
          axios.get("/products").then(r => r.data || []),
          axios.get("/sucursal/" + this.sucursalId + "/products/all/prices").then(r => r.data || []),
          axios.get("/products/all/prices").then(r => r.data || []),
          axios.get("/restaurant/areas").then(r => r.data || []),
          axios.get("/groups").then(r => r.data || []).catch(() => [])
        ]);
        this.sucursal = sucursal || {};
        // Solo productos activos: los inactivos no se venden y el POS no los muestra.
        this.products = products.filter(p => p.status === "ACTIVE");
        this.branchPrices = branchPrices;
        this.masterPrices = masterPrices;
        this.areas = areas.filter(a => !a.status || a.status === "ACTIVE");
        this.groups = groups;
      } catch (err) {
        this.loadError = "No se pudo cargar el menú: " + this.errText(err);
      } finally {
        this.loading = false;
      }
    },
    async reloadBranchPrices() {
      const r = await axios.get("/sucursal/" + this.sucursalId + "/products/all/prices");
      this.branchPrices = r.data || [];
    },
    errText(err) {
      return (err.response && (err.response.data.message || err.response.data)) || err.message;
    },
    masterPrice(productId, areaId) {
      return this.masterPrices.find(p => p.product_id === productId && p.area_restaurant_id === areaId) || null;
    },
    cellKey(item, area) {
      return item.product.id + "-" + area.id;
    },
    cellVariant(item, area) {
      const c = item.cells[area.id];
      if (!c) return "light";
      if (!c.status) return "secondary";
      return c.follow_price ? "success" : "danger";
    },
    cellText(item, area) {
      const c = item.cells[area.id];
      if (!c) return "sin precio";
      if (!c.status) return "-";
      return "$" + Number(c.price).toFixed(2);
    },
    cellTitle(item, area) {
      const c = item.cells[area.id];
      if (!c) return "Sin precio en esta sucursal. Clic para crear.";
      if (!c.status) return "Deshabilitado ($" + Number(c.price).toFixed(2) + "). Clic para opciones.";
      return c.follow_price ? "Sigue el precio master" : "Precio único de esta sucursal";
    },

    // ---------- edición inline ----------
    startEdit(item, area) {
      const c = item.cells[area.id];
      if (!c || !c.status) {
        this.openOptions(item, area);
        return;
      }
      this.editingKey = this.cellKey(item, area);
      this.editingValue = Number(c.price) || 0;
    },
    cancelInline() {
      this.editingKey = null;
      this.editingValue = 0;
    },
    saveInline(item, area) {
      if (this.editingKey !== this.cellKey(item, area)) return;
      const c = item.cells[area.id];
      const value = this.editingValue;
      this.editingKey = null;
      if (!c || value === Number(c.price) || value === "" || value == null) return;

      const master = this.masterPrice(item.product.id, area.id);
      if (master && Number(master.price) === Number(value) && !c.follow_price) {
        this.$bvModal
          .msgBoxConfirm(
            "El precio es igual al master ($" + Number(master.price).toFixed(2) + "). ¿Quieres que esta sucursal siga el precio master?",
            { title: "¿Seguir precio master?", okTitle: "Sí, seguir master", cancelTitle: "No, mantener único", okVariant: "success", cancelVariant: "danger", centered: true }
          )
          .then(follow => this.updatePrice(c, { price: value, follow_price: follow === true }));
        return;
      }
      this.updatePrice(c, { price: value, follow_price: false });
    },
    async updatePrice(record, changes) {
      this.saving = true;
      try {
        const payload = Object.assign({}, record, changes, { sucursal_id: this.sucursalId });
        const r = await axios.put("/product/price/" + record.id, payload);
        const updated = (r.data && r.data.id) ? r.data : payload;
        const idx = this.branchPrices.findIndex(p => p.id === record.id);
        if (idx >= 0) this.$set(this.branchPrices, idx, updated);
        this.makeToast("Éxito", "Precio actualizado" + (updated.follow_price ? " (sigue master)" : ""), "success");
      } catch (err) {
        this.makeToast("Error", this.errText(err), "danger");
      } finally {
        this.saving = false;
      }
    },
    async createPrice(price) {
      this.saving = true;
      try {
        await axios.post("/product/price", Object.assign({}, price, { id: 0, sucursal_id: this.sucursalId }));
        await this.reloadBranchPrices();
        this.makeToast("Éxito", "Precio creado", "success");
      } catch (err) {
        this.makeToast("Error", this.errText(err), "danger");
      } finally {
        this.saving = false;
      }
    },

    // ---------- modal de opciones ----------
    openOptions(item, area) {
      this.optionsProduct = item.product;
      this.optionsArea = area;
      const c = item.cells[area.id];
      if (c) {
        this.actualPrice = Object.assign({}, c);
      } else {
        const master = this.masterPrice(item.product.id, area.id);
        this.actualPrice = Object.assign(this.emptyPrice(), {
          product_id: item.product.id,
          area_restaurant_id: area.id,
          price: master ? master.price : 0,
          price_wo_tax: master ? master.price_wo_tax : 0,
          tax: master ? master.tax : 0,
          tax_excempt: master ? master.tax_excempt : false,
          billable: master ? master.billable : true,
          follow_price: !!master
        });
      }
      this.$bvModal.show("modal-menu-price");
    },
    async saveOptions() {
      if (this.actualPrice.follow_price && !this.optionsMaster) {
        this.makeToast("Aviso", "No hay precio master para seguir en esta área; captura un precio único.", "warning");
        return;
      }
      if (this.actualPrice.id > 0) {
        const original = this.branchPrices.find(p => p.id === this.actualPrice.id);
        await this.updatePrice(original || this.actualPrice, {
          price: this.actualPrice.price,
          follow_price: this.actualPrice.follow_price,
          status: this.actualPrice.status
        });
      } else {
        await this.createPrice(this.actualPrice);
      }
      this.$bvModal.hide("modal-menu-price");
    },
    deletePrice() {
      const price = this.actualPrice;
      this.$bvModal
        .msgBoxConfirm("¿Eliminar el precio de " + this.optionsTitle + " en esta sucursal?", {
          title: "Eliminar precio", okTitle: "Eliminar", okVariant: "danger", cancelTitle: "Cancelar", centered: true
        })
        .then(async ok => {
          if (!ok) return;
          try {
            await axios.delete("/product/price/" + price.id);
            this.branchPrices = this.branchPrices.filter(p => p.id !== price.id);
            this.$bvModal.hide("modal-menu-price");
            this.makeToast("Éxito", "Precio eliminado", "success");
          } catch (err) {
            this.makeToast("Error", this.errText(err), "danger");
          }
        });
    },

    // ---------- acciones masivas por área sobre los productos filtrados ----------
    async runBatch(targets, changes, label) {
      if (targets.length === 0) {
        this.makeToast("Info", "No hay precios que cambiar en los productos filtrados", "info");
        return;
      }
      this.batchProcessing = true;
      let okCount = 0;
      let failCount = 0;
      for (const t of targets) {
        try {
          const payload = Object.assign({}, t, changes, { sucursal_id: this.sucursalId });
          const r = await axios.put("/product/price/" + t.id, payload);
          const updated = (r.data && r.data.id) ? r.data : payload;
          const idx = this.branchPrices.findIndex(p => p.id === t.id);
          if (idx >= 0) this.$set(this.branchPrices, idx, updated);
          okCount++;
        } catch (e) {
          failCount++;
        }
      }
      this.batchProcessing = false;
      this.makeToast(failCount ? "Parcial" : "Éxito", okCount + " precios " + label + (failCount ? ", " + failCount + " con error" : ""), failCount ? "warning" : "success");
    },
    batchTargets(predicate) {
      const areaId = this.batchArea;
      return this.filteredRows.map(r => r.cells[areaId]).filter(c => c && predicate(c));
    },
    batchFollowMaster() {
      const targets = this.batchTargets(c => !c.follow_price && this.masterPrice(c.product_id, c.area_restaurant_id));
      this.runBatch(targets, { follow_price: true }, "ahora siguen master");
    },
    batchToggleStatus(status) {
      const targets = this.batchTargets(c => c.status !== status);
      this.runBatch(targets, { status }, status ? "habilitados" : "deshabilitados");
    },
    makeToast(title, content, variant) {
      this.$bvToast.toast(content, { title, variant, solid: true, autoHideDelay: 4000, appendToast: true });
    }
  }
};
</script>

<style scoped>
.price-badge {
  font-size: 0.9em;
  padding: 6px 10px;
  cursor: pointer;
  min-width: 70px;
  text-align: center;
  transition: opacity 0.15s;
}
.price-badge:hover {
  opacity: 0.85;
}
.menu-table >>> td,
.menu-table >>> th {
  vertical-align: middle;
  white-space: nowrap;
}
</style>
